// web/src/components/StoreProductPanel.tsx
import React, { useEffect, useState } from "react";
import { useViewport } from "../hooks/useViewport";
import type { GooglePlayBillingPlugin } from "../types/google-play-billing";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";

// Get the GooglePlayBilling plugin from Capacitor
const getGooglePlayBilling = (): GooglePlayBillingPlugin | null => {
  console.log("[Billing] getGooglePlayBilling called");
  
  if (typeof window === 'undefined') {
    console.log("[Billing] window is undefined");
    return null;
  }
  
  const Capacitor = (window as any).Capacitor;
  console.log("[Billing] Capacitor object:", Capacitor ? "exists" : "null");
  
  if (Capacitor) {
    console.log("[Billing] Capacitor.Plugins:", Capacitor.Plugins ? "exists" : "null");
    console.log("[Billing] Available plugins:", Capacitor.Plugins ? Object.keys(Capacitor.Plugins) : "none");
    
    // Method 1: Direct plugin access
    if (Capacitor.Plugins?.GooglePlayBilling) {
      console.log("[Billing] Found plugin via Capacitor.Plugins.GooglePlayBilling");
      return Capacitor.Plugins.GooglePlayBilling;
    }
    
    // Method 2: Try getPlugin
    if (Capacitor.getPlugin) {
      try {
        const plugin = Capacitor.getPlugin('GooglePlayBilling') as GooglePlayBillingPlugin;
        if (plugin) {
          console.log("[Billing] Found plugin via Capacitor.getPlugin");
          return plugin;
        }
      } catch (e) {
        console.warn("[Billing] Failed to get GooglePlayBilling plugin via getPlugin:", e);
      }
    }
    
    // Method 3: Try Plugins object directly
    if ((window as any).Plugins?.GooglePlayBilling) {
      console.log("[Billing] Found plugin via window.Plugins.GooglePlayBilling");
      return (window as any).Plugins.GooglePlayBilling;
    }
    
    console.log("[Billing] Plugin not found in any location");
  } else {
    console.log("[Billing] Capacitor object is null");
  }
  
  return null;
};

interface Product {
  id: string;
  name: string;
  description: string;
  base_entries: number;
  usd_price: number;
  has_bonus: boolean;
  bonus_text?: string;
}

interface StoreProductPanelProps {
  product: Product;
  onClose: () => void;
  onPurchaseSuccess: () => void;
  position?: { x: number; y: number } | null;
  account?: { email_verified: boolean; email?: string } | null;
  onShowCompleteRegistration?: () => void;
}

export default function StoreProductPanel({
  product,
  onClose,
  onPurchaseSuccess,
  position,
  account,
  onShowCompleteRegistration,
}: StoreProductPanelProps) {
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googlePlayPrice, setGooglePlayPrice] = useState<string | null>(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isMobile = viewport.isMobile;
  
  // Detect if running on Android
  const isAndroid = typeof window !== 'undefined' && 
                    (window as any).Capacitor && 
                    (window as any).Capacitor.getPlatform() === 'android';
  
  // Log platform detection
  useEffect(() => {
    console.log("[Billing] Platform detection:", {
      hasWindow: typeof window !== 'undefined',
      hasCapacitor: typeof window !== 'undefined' && !!(window as any).Capacitor,
      platform: typeof window !== 'undefined' && (window as any).Capacitor ? (window as any).Capacitor.getPlatform() : 'unknown',
      isAndroid: isAndroid
    });
  }, [isAndroid]);
  
  // Add diagnostic function
  async function runDiagnostics() {
    console.log("[Billing] === RUNNING DIAGNOSTICS ===");
    
    const diagnostics: any = {
      platform: isAndroid ? 'android' : 'web',
      hasWindow: typeof window !== 'undefined',
      hasCapacitor: typeof window !== 'undefined' && !!(window as any).Capacitor,
      pluginAccessible: false,
      pluginDiagnostics: null,
      error: null
    };
    
    if (isAndroid) {
      try {
        const billing = getGooglePlayBilling();
        diagnostics.pluginAccessible = !!billing;
        
        if (billing && (billing as any).getDiagnostics) {
          try {
            const pluginDiag = await (billing as any).getDiagnostics();
            diagnostics.pluginDiagnostics = pluginDiag;
            console.log("[Billing] Plugin diagnostics:", pluginDiag);
          } catch (e) {
            diagnostics.error = "Failed to get plugin diagnostics: " + e;
          }
        }
      } catch (e) {
        diagnostics.error = "Failed to access plugin: " + e;
      }
    }
    
    console.log("[Billing] Full diagnostics:", JSON.stringify(diagnostics, null, 2));
    console.log("[Billing] === DIAGNOSTICS COMPLETE ===");
    
    return diagnostics;
  }

  // Query Google Play product details on Android when panel opens
  useEffect(() => {
    if (isAndroid) {
      console.log("[Billing] Android detected, will query prices for product:", product.id);
      // Run diagnostics first
      runDiagnostics();
      // Delay slightly to ensure plugin is loaded
      const timer = setTimeout(() => {
        queryGooglePlayPrice();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      console.log("[Billing] Not Android, skipping price query");
    }
  }, [isAndroid, product.id]);
  
  async function queryGooglePlayPrice() {
    console.log("[Billing] queryGooglePlayPrice called for product:", product.id);
    // Try to get billing plugin with retry
    let billing = getGooglePlayBilling();
    if (!billing) {
      console.log("[Billing] Plugin not found on first try, waiting 500ms...");
      // Wait a bit and try again (plugin might still be loading)
      await new Promise(resolve => setTimeout(resolve, 500));
      billing = getGooglePlayBilling();
    }
    
    if (!billing) {
      console.warn("[Billing] Google Play Billing plugin not available - using USD price");
      return;
    }
    
    console.log("[Billing] Plugin found, querying products...");
    setLoadingPrice(true);
    try {
      const result = await billing.queryProducts({
        productIds: [product.id]
      });
      
      console.log("[Billing] Query result:", result);
      if (result.products && result.products.length > 0) {
        const googleProduct = result.products[0];
        console.log("[Billing] Got product price:", googleProduct.price);
        setGooglePlayPrice(googleProduct.price);
      } else {
        console.warn("[Billing] No products returned from Google Play");
      }
    } catch (err: any) {
      console.error("[Billing] Failed to query Google Play price:", err);
      // Don't show error - just fall back to USD price
    } finally {
      setLoadingPrice(false);
    }
  }

  // Safety net: ensure we don't stay in a loading state forever on emulators
  useEffect(() => {
    if (!isAndroid) return;
    if (!loadingPrice) return;
    const fallback = setTimeout(() => {
      if (!googlePlayPrice) {
        setGooglePlayPrice(`$${product.usd_price.toFixed(2)} USD`);
      }
      setLoadingPrice(false);
    }, 2500);
    return () => clearTimeout(fallback);
  }, [isAndroid, loadingPrice, googlePlayPrice, product.usd_price]);

  // Handle ESC key
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  async function handlePurchase() {
    // Check if user is verified - if not, show complete registration modal
    if (account && !account.email_verified) {
      onClose();
      if (onShowCompleteRegistration) {
        onShowCompleteRegistration();
      } else {
        // Fallback: navigate to home with complete registration modal
        window.location.href = '/?showCompleteRegistration=true';
      }
      return;
    }

    setPurchasing(true);
    setError(null);

    try {
      // Android: Use Google Play Billing
      if (isAndroid) {
        await handleAndroidPurchase();
      } else {
        // Web: Use Stripe
        await handleWebPurchase();
      }
    } catch (err: any) {
      setError(err.message || "Failed to process purchase. Please try again.");
      setPurchasing(false);
    }
  }

  async function handleAndroidPurchase() {
    console.log("[Billing] handleAndroidPurchase called for product:", product.id);
    
    // Run diagnostics first
    const diag = await runDiagnostics();
    
    // Try to get billing plugin with retry
    let billing = getGooglePlayBilling();
    if (!billing) {
      console.log("[Billing] Plugin not found on first try, waiting 500ms...");
      // Wait a bit and try again (plugin might still be loading)
      await new Promise(resolve => setTimeout(resolve, 500));
      billing = getGooglePlayBilling();
    }
    
    if (!billing) {
      // Try to get diagnostics if available
      let diagInfo = "Plugin not accessible";
      try {
        if ((billing as any)?.getDiagnostics) {
          const pluginDiag = await (billing as any).getDiagnostics();
          diagInfo = JSON.stringify(pluginDiag, null, 2);
        } else {
          diagInfo = JSON.stringify(diag, null, 2);
        }
      } catch (e) {
        diagInfo = "Could not get diagnostics: " + e + "\n\nPrevious diagnostics: " + JSON.stringify(diag, null, 2);
      }
      
      const errorMsg = `Google Play Billing is not available.\n\nDiagnostics:\n${diagInfo}\n\nPlease ensure:\n1. App is installed from Play Store\n2. You are signed in with a test account\n3. License Testing is enabled`;
      console.error("[Billing]", errorMsg);
      throw new Error(errorMsg);
    }
    
    // Get diagnostics before purchase
    try {
      if ((billing as any).getDiagnostics) {
        const pluginDiag = await (billing as any).getDiagnostics();
        console.log("[Billing] Plugin diagnostics:", pluginDiag);
        
        if (!pluginDiag.billingReady) {
          throw new Error("Billing client is not ready yet. Please wait a moment and try again.");
        }
      }
    } catch (e) {
      console.warn("[Billing] Could not get plugin diagnostics:", e);
    }
    
    console.log("[Billing] Plugin found, launching purchase flow...");

    try {
      // Step 1: Launch native purchase flow
      console.log("[Billing] Calling purchaseProduct with productId:", product.id);
      const purchase = await billing.purchaseProduct({
        productId: product.id
      });

      console.log("[Billing] Purchase result:", purchase);
      if (!purchase || !purchase.purchaseToken) {
        console.error("[Billing] Purchase failed or cancelled - no purchase token");
        throw new Error("Purchase was cancelled or failed");
      }

      console.log("[Billing] Purchase successful, verifying with backend...");
      // Step 2: Verify purchase with backend
      const verifyRes = await fetch(`${API_BASE}/store/verify-google-play`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: product.id,
          purchase_token: purchase.purchaseToken,
          package_name: "ai.goldrush.app",
          order_id: purchase.orderId || undefined
        }),
      });

      if (!verifyRes.ok) {
        const errorText = await verifyRes.text();
        throw new Error(errorText || "Purchase verification failed");
      }

      const verifyData = await verifyRes.json();
      console.log("[Billing] Backend verification response:", verifyData);

      if (!verifyData.ok) {
        console.error("[Billing] Backend verification failed:", verifyData.message);
        throw new Error(verifyData.message || "Purchase verification failed");
      }

      console.log("[Billing] Backend verification successful, consuming purchase...");
      // Step 3: Consume the purchase (required for consumable products)
      // This MUST succeed or the purchase will remain and block future purchases
      try {
        await billing.consumePurchase({
          purchaseToken: purchase.purchaseToken
        });
        console.log("[Billing] Purchase consumed successfully");
      } catch (consumeErr: any) {
        // Consumption failure is CRITICAL - log and throw
        console.error("[Billing] Failed to consume purchase - this will block future purchases:", consumeErr);
        throw new Error(`Purchase verified but failed to consume: ${consumeErr.message || consumeErr}. The purchase may block future purchases.`);
      }

      // Step 4: Success - refresh account and close panel
      console.log("[Billing] Purchase process completed successfully");
      setPurchasing(false);
      if (onPurchaseSuccess) {
        onPurchaseSuccess();
      }
      onClose();

    } catch (err: any) {
      console.error("[Billing] Purchase flow error:", err);
      // Handle user cancellation gracefully
      if (err.message && (
        err.message.includes("cancelled") || 
        err.message.includes("canceled") ||
        err.message.includes("USER_CANCELED")
      )) {
        console.log("[Billing] Purchase cancelled by user");
        setError(null); // Don't show error for user cancellation
        setPurchasing(false);
        return;
      }
      throw err; // Re-throw other errors
    }
  }

  async function handleWebPurchase() {
    const res = await fetch(`${API_BASE}/store/checkout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: product.id }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || "Failed to create checkout session");
    }

    const data = await res.json();
    
    if (data.checkout_url) {
      // Detect if we're on itch.io (iframe context)
      const isItch = window.location.hostname.endsWith('itch.io') || 
                     window.location.hostname.endsWith('itch.zone') ||
                     window.self !== window.top; // Also check if we're in an iframe
      
      if (isItch) {
        // On itch.io - MUST open in new window, never redirect in iframe
        // Close panel first to prevent any iframe context issues
        // This ensures the component is unmounted before opening Stripe
        setPurchasing(false);
        onClose();
        
        // Use setTimeout to ensure panel is fully unmounted before opening new window
        // This prevents Stripe from detecting iframe context and trying to load in the iframe
        // Longer delay ensures React has time to unmount the component
        setTimeout(() => {
          window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
        }, 100);
      } else {
        // On regular site - redirect directly (normal flow, no new window)
        window.location.href = data.checkout_url;
      }
    } else {
      throw new Error("No checkout URL received");
    }
  }

  return (
    <>
      {/* Background Dim Overlay */}
      <div
        className="fixed inset-0 bg-black transition-opacity duration-300 z-40"
        style={{ opacity: 0.1 }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed z-50 border-2 transition-all duration-300 ease-out ${isMobileLandscape ? 'rounded-xl' : 'rounded-2xl'}`}
        style={{
          width: isMobileLandscape ? "calc(100vw - env(safe-area-inset-left, 0px) - 32px - 112px - env(safe-area-inset-right, 0px) - 48px + 20px - 16px)" : (isMobile ? "calc(100vw - 2rem)" : "400px"),
          maxWidth: isMobile ? "420px" : "400px",
          maxHeight: isMobileLandscape ? "calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 1rem)" : "80vh",
          background: "linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)",
          borderColor: "#8b6542",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          left: isMobileLandscape ? "calc(env(safe-area-inset-left, 0px) + 32px + 112px - 20px + 8px)" : "50%",
          top: isMobileLandscape ? "env(safe-area-inset-top, 0px)" : "50%",
          bottom: isMobileLandscape ? "env(safe-area-inset-bottom, 0px)" : "auto",
          transform: isMobileLandscape ? "none" : "translate(-50%, -50%)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className={`absolute flex items-center justify-center rounded-full transition-all hover:bg-black hover:bg-opacity-20 ${isMobileLandscape ? 'top-2 right-2 w-7 h-7' : 'top-4 right-4 w-8 h-8'}`}
          style={{ color: "#3e2615", zIndex: 100, pointerEvents: 'auto' }}
          aria-label="Close"
        >
          <svg width={isMobileLandscape ? "18" : "20"} height={isMobileLandscape ? "18" : "20"} viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 6.586L3.293 1.879 1.879 3.293 6.586 8l-4.707 4.707 1.414 1.414L8 9.414l4.707 4.707 1.414-1.414L9.414 8l4.707-4.707-1.414-1.414L8 6.586z" />
          </svg>
        </button>

        {/* Content - scrollable area */}
        <div 
          className={`overflow-y-auto flex-1 ${isMobileLandscape ? 'p-3 pt-7' : 'p-6 pt-10'}`}
          style={{ 
            maxHeight: isMobileLandscape ? "calc(100% - 72px)" : "calc(80vh - 56px)"
          }}
        >
          {/* Header */}
          {isMobileLandscape ? (
            <div className="text-center mb-3">
              <h2
                className="font-bold leading-tight text-xl"
                style={{ color: "#3e2615" }}
              >
                {product.name}
              </h2>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3 mb-3">
              <h2 className={`font-bold ${isMobile ? 'text-xl leading-tight' : 'text-2xl mb-1'}`} style={{ color: "#3e2615" }}>
                {product.name}
              </h2>
              <div className="font-extrabold text-right text-3xl" style={{ color: "#3e2615" }}>
                {isAndroid && loadingPrice
                  ? "Loading..."
                  : (isAndroid && googlePlayPrice) ? googlePlayPrice : `$${product.usd_price.toFixed(2)} USD`}
              </div>
            </div>
          )}

          {/* Description */}
          <p
            className={`${isMobileLandscape ? 'mb-3' : 'mb-4'}`}
            style={{
              color: "#3e2615",
              fontSize: isMobileLandscape ? "clamp(15px, 2.8vw, 17px)" : undefined,
              lineHeight: 1.35,
            }}
          >
            {product.description}
          </p>

          {/* Entries Info */}
          <div className={`rounded-lg ${isMobileLandscape ? 'p-3 mb-3' : 'p-4 mb-4'}`} style={{ background: "rgba(255, 255, 255, 0.35)" }}>
            <div
              className="font-semibold"
              style={{
                color: "#3e2615",
                fontSize: isMobileLandscape ? "clamp(16px, 3vw, 18px)" : undefined,
              }}
            >
              {product.base_entries} Entry Tickets
            </div>
            {product.has_bonus && product.bonus_text && (
              <div
                className={isMobileLandscape ? 'mt-1' : 'mt-2'}
                style={{
                  color: "#1a0f08",
                  fontSize: isMobileLandscape ? "clamp(14px, 2.6vw, 16px)" : undefined,
                  lineHeight: 1.35,
                }}
              >
                {product.bonus_text}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className={`mb-2 p-2 rounded-lg bg-red-100 border border-red-400 text-red-700 ${isMobileLandscape ? 'text-xs mb-2' : 'text-sm mb-4 p-3'}`}>
              {error}
            </div>
          )}
        </div>

        {/* BUY section - sticky at bottom */}
        <div className={`flex-shrink-0 border-t border-[#8b6542] ${isMobileLandscape ? 'p-3 pb-3 pt-2' : 'p-6 pt-4'}`}>
          {isMobileLandscape && (
            <div
              className="font-extrabold text-center mb-2"
              style={{
                color: "#3e2615",
                fontSize: "clamp(20px, 4vw, 26px)",
                lineHeight: 1.1,
              }}
            >
              {isAndroid && loadingPrice
                ? "Loading..."
                : (isAndroid && googlePlayPrice) ? googlePlayPrice : `$${product.usd_price.toFixed(2)} USD`}
            </div>
          )}
          <button
            onClick={handlePurchase}
            disabled={purchasing}
            className={`w-full rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isMobileLandscape ? 'py-3 text-base' : 'py-4 text-lg'}`}
            style={{
              background: "#fffaeb",
              color: "#3e2615",
              boxShadow: "0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)",
              border: "2px solid #8b6542",
            }}
            onMouseEnter={(e) => {
              if (!purchasing) {
                e.currentTarget.style.transform = "translateY(-2px)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
            onMouseDown={(e) => {
              if (!purchasing) {
                e.currentTarget.style.transform = "translateY(2px)";
              }
            }}
            onMouseUp={(e) => {
              if (!purchasing) {
                e.currentTarget.style.transform = "translateY(-2px)";
              }
            }}
          >
            {purchasing ? "Processing..." : "BUY NOW"}
          </button>
        </div>
      </div>
    </>
  );
}
